import * as React from "react";
import { useEffect, useState } from "react";
import axios from "axios";
import { useHistory } from "react-router-dom";
import { Button, CircularProgress, TableCell, TableRow } from "@material-ui/core";
import { Notification, useNotify } from "react-admin";
import { ENDPOINTS } from "../apiConstants";
import { getConfigGet } from "../getConfig";
import { approveInvoice } from "./approve";
import { BrandPicker, brandLabel, useBrandNames } from "./brands";
import {
  DataTable,
  FilterSelect,
  INVOICE_STATUSES,
  PAGE_SIZE,
  PageHeader,
  ServerPagination,
  StatusChip,
  errorMessage,
  fmtDate,
  fmtPeriod,
  gbp,
  monthOptions,
  plainOptions,
  useCloStyles,
} from "./shared";

type Filters = { brandId: string; period: string; status: string };

const EMPTY_FILTERS: Filters = { brandId: "", period: "", status: "" };

const COLUMNS = [
  { label: "Invoice" },
  { label: "Brand" },
  { label: "Period" },
  { label: "Part" },
  { label: "Issued" },
  { label: "Due" },
  { label: "Lines", numeric: true },
  { label: "Subtotal", numeric: true },
  { label: "VAT", numeric: true },
  { label: "Total", numeric: true },
  { label: "Status" },
  { label: "Actions" },
];

const InvoicesList = () => {
  const classes = useCloStyles();
  const notify = useNotify();
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [invoices, setInvoices] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
  const [approvingId, setApprovingId] = useState<number | null>(null);
  const names = useBrandNames(invoices.map((i) => i.brand_id));

  // Overrides exist because setState has not applied yet when Clear re-fetches.
  const fetchInvoices = async (nextPage = 1, overrides?: Filters) => {
    const active = overrides || filters;
    const params: any = { page: nextPage, pageSize: PAGE_SIZE };
    if (active.brandId) params.brand_id = active.brandId;
    if (active.period) params.period = active.period;
    if (active.status) params.status = active.status;
    try {
      setLoading(true);
      const res: any = await axios.get(ENDPOINTS.CLO_INVOICES, getConfigGet(params));
      setInvoices(res.data?.data || []);
      setTotal(res.data?.total || 0);
      setPage(nextPage);
    } catch (err) {
      notify(errorMessage(err, "Failed to load invoices"), "warning");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInvoices();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const clear = () => {
    setFilters(EMPTY_FILTERS);
    fetchInvoices(1, EMPTY_FILTERS);
  };

  const approve = async (invoice: any) => {
    try {
      setApprovingId(invoice.id);
      const result = await approveInvoice(invoice, brandLabel(names, invoice.brand_id));
      if (!result) return;
      notify(`Approved invoices for ${brandLabel(names, result.brandId)}, ${fmtPeriod(result.period)}`, "info");
      fetchInvoices(page);
    } catch (err) {
      notify(errorMessage(err, "Failed to approve invoice"), "warning");
    } finally {
      setApprovingId(null);
    }
  };

  const filtered = !!(filters.brandId || filters.period || filters.status);

  return (
    <div className={classes.main}>
      <PageHeader title="Invoices" subtitle="Monthly retailer invoices: a fee part and a reward reimbursement part" />

      <div className={classes.filterContainer}>
        <BrandPicker
          className={classes.filterField}
          value={filters.brandId}
          onChange={(brandId) => setFilters({ ...filters, brandId })}
        />
        <FilterSelect
          label="Period"
          value={filters.period}
          onChange={(period) => setFilters({ ...filters, period })}
          options={monthOptions()}
        />
        <FilterSelect
          label="Status"
          value={filters.status}
          onChange={(status) => setFilters({ ...filters, status })}
          options={plainOptions(INVOICE_STATUSES)}
        />
        <Button className={classes.filterButton} variant="contained" color="primary" disableElevation onClick={() => fetchInvoices(1)} disabled={loading}>
          Search
        </Button>
        <Button className={classes.filterButton} variant="outlined" onClick={clear}>Clear</Button>
      </div>

      {loading ? (
        <div className={classes.loadingContainer}><CircularProgress size={40} /></div>
      ) : (
        <DataTable
          columns={COLUMNS}
          isEmpty={invoices.length === 0}
          empty={
            filtered
              ? "No invoices match these filters."
              : "No invoices yet. Invoices are drafted automatically on the 6th of each month for the previous month."
          }
          footer={<ServerPagination page={page} total={total} onChange={fetchInvoices} />}
        >
          {invoices.map((inv) => (
            <TableRow
              key={inv.id}
              hover
              className={classes.clickableRow}
              onClick={() => history.push(`/clo/invoices/${inv.id}`)}
            >
              <TableCell>{inv.external_invoice_number || `#${inv.id}`}</TableCell>
              <TableCell>{brandLabel(names, inv.brand_id)}</TableCell>
              <TableCell>{fmtPeriod(inv.period)}</TableCell>
              <TableCell>{inv.part}</TableCell>
              <TableCell>{fmtDate(inv.issued_on)}</TableCell>
              <TableCell>{fmtDate(inv.due_on)}</TableCell>
              <TableCell align="right">{inv.line_count}</TableCell>
              <TableCell align="right">{gbp(inv.subtotal_minor)}</TableCell>
              <TableCell align="right">{gbp(inv.vat_minor)}</TableCell>
              <TableCell align="right"><strong>{gbp(inv.total_minor)}</strong></TableCell>
              <TableCell><StatusChip status={inv.status} /></TableCell>
              <TableCell onClick={(e) => e.stopPropagation()}>
                {inv.status === "draft" ? (
                  <Button
                    size="small"
                    variant="outlined"
                    color="primary"
                    disabled={approvingId === inv.id}
                    onClick={() => approve(inv)}
                  >
                    Approve
                  </Button>
                ) : (
                  "—"
                )}
              </TableCell>
            </TableRow>
          ))}
        </DataTable>
      )}
      <Notification />
    </div>
  );
};

export default InvoicesList;
